import { styled } from '@mui/system';
import ThemedDialog from 'Components/_NewElements/Dialog';
import { Container, Item } from 'Components/Grid';
import { mergeRight } from 'ramda';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { actions } from '../../store/documents/actions';
import InputHandler from '../InputHandler';

import type { TDispatch } from 'Store/index';

const StyledRowItem = styled(Item)({});

const InputRowContainer = styled(Container)({
  margin: '0 0 3rem 0',
});

const optionFormatter = (n: string | number): React.JSX.Element => <FormattedMessage id={'general.opts.' + n} />;

type Props = IOwnProps & IDispatchFromProps;

/**
 * Delete dialog
 */
class DeleteDialog extends React.Component<Props, { deleteDetails: IDeleteDetails; isBusy: boolean }> {
  // This flag is set synchronously to prevent two click handlers firing in the same tick.
  // UI is driven by this.state.isBusy (updated asynchronously by setState).
  private _busy = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      deleteDetails: {
        date: undefined,
        reason: '',
        reasonOther: '',
      },
      isBusy: false,
    };
  }

  public componentDidUpdate(prevProps: Props): void {
    // Release the lock when the dialog is reopened so a subsequent deletion can proceed
    if (this.props.open && !prevProps.open) {
      this._busy = false;
      this.setState({ isBusy: false });
    }
  }

  private onDeleteClick = (e: React.MouseEvent<Element>): void => {
    if (this._busy) return;
    if (this.props.customDeleteHandler) {
      this._busy = true;
      this.setState({ isBusy: true });
      this.props.customDeleteHandler(this.state.deleteDetails);
    } else {
      // deleteHandler sets the lock itself, but only once it actually proceeds, so an invalid (no-op) click does not wedge the dialog
      this.deleteHandler(e);
    }
  };

  public onChangeDetail = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    // deepcode ignore ReactNextState: Needs rework to fix
    this.setState({
      deleteDetails: mergeRight(this.state.deleteDetails, {
        [name]: value as any,
      }),
    });
  };

  public deleteHandler = (e: React.MouseEvent<Element>): void => {
    const { _id, _type } = this.props.document;
    const { date, reason, reasonOther } = this.state.deleteDetails;
    const { subDocs } = this.props;
    if (date && reason) {
      if (this._busy) return;
      this._busy = true;
      this.setState({ isBusy: true });
      if (_type === 'filestorefile') {
        this.props.deleteFile(_id, { date, reason, reasonOther });
      } else {
        this.props.deleteDocument({ name: _type, id: _id }, { date, reason, reasonOther }, subDocs);
      }
      this.props.setOpen(e);
    }
  };

  public render(): React.JSX.Element {
    // Custom content replaces the deletion details form, so there are no date/reason inputs to require
    const detailsRequired = !this.props.customDeleteContent;

    return (
      <ThemedDialog
        open={this.props.open}
        title={<FormattedMessage id="general.deletionTitle" />}
        dialogActions={[
          { text: 'general.cancel', onClick: this.props.setOpen, border: false },
          {
            text: 'general.delete',
            onClick: this.onDeleteClick,
            width: 15,
            height: 4,
            fontSize: 16,
            loading: this.state.isBusy,
            disabled: detailsRequired && (!this.state.deleteDetails.date || !this.state.deleteDetails.reason),
          },
        ]}
      >
        <>
          {this.props.customDeleteContent ?? (
            <>
              <InputRowContainer>
                <StyledRowItem xs={4}>
                  <FormattedMessage id="general.date" />
                </StyledRowItem>
                <StyledRowItem xs={8}>
                  <InputHandler
                    type="PartialDate"
                    editing={true}
                    name="date"
                    formData={{
                      onChange: this.onChangeDetail,
                      document: { date: this.state.deleteDetails.date },
                    }}
                    dateDefault="now"
                    isNotCancellable={true}
                  />
                </StyledRowItem>
              </InputRowContainer>
              <InputRowContainer>
                <StyledRowItem xs={4}>
                  <FormattedMessage id="general.reason" />
                </StyledRowItem>
                <StyledRowItem xs={8}>
                  <InputHandler
                    type="Radio"
                    editing={true}
                    name="reason"
                    formData={{
                      onChange: this.onChangeDetail,
                      document: { reason: this.state.deleteDetails.reason },
                    }}
                    options={['duplicate', 'mistake', 'other']}
                    optionFormatter={optionFormatter}
                  />
                </StyledRowItem>
              </InputRowContainer>
              <InputRowContainer>
                <StyledRowItem xs={4}>
                  <FormattedMessage id="general.reasonOther" />
                </StyledRowItem>
                <StyledRowItem xs={8}>
                  <InputHandler
                    type="TextField"
                    editing={true}
                    name="reasonOther"
                    formData={{
                      onChange: this.onChangeDetail,
                      document: { reasonOther: this.state.deleteDetails.reasonOther },
                    }}
                    placeholder={'general.reasonOther'}
                  />
                </StyledRowItem>
              </InputRowContainer>
            </>
          )}
        </>
      </ThemedDialog>
    );
  }
}

export interface IDeleteDetails {
  date?: PartialDate;
  reason: string;
  reasonOther: string;
}

interface IDispatchFromProps {
  deleteDocument: (
    editingDocument: { name: string; id: string },
    reason: { [key: string]: string | PartialDate },
    subDocs?: Array<{ name: string; id: string } | undefined>,
  ) => void;
  deleteFile: (id: string, reason: { date: PartialDate; reason: string; reasonOther?: string }) => void;
}

const mapDispatchToProps = (dispatch: TDispatch) => ({
  deleteDocument: (
    editingDocument: { name: string; id: string },
    reason: { [key: string]: string | PartialDate },
    subDocs?: Array<{ name: string; id: string } | undefined>,
  ) => dispatch(actions.deleteDocument(editingDocument, reason, subDocs)),
  deleteFile: (id: string, reason: { date: PartialDate; reason: string; reasonOther?: string }) =>
    dispatch(actions.deleteFileData(id, reason)),
});

interface IOwnProps {
  document: IDocumentV2;
  open: boolean;
  setOpen: (e: React.MouseEvent<Element>) => void;
  subDocs?: Array<{ name: string; id: string } | undefined>;
  customDeleteContent?: React.JSX.Element;
  customDeleteHandler?: (deleteDetails?: IDeleteDetails) => void;
}

export default connect<IOwnProps, IDispatchFromProps>(null, mapDispatchToProps)(DeleteDialog);
